// Repair on one drone. Nobody chooses a mode: scan a board or a QR part and it is fitted
// to this drone through the same commit the Assembly bench uses (Assembly/commitFit); scan
// a box and say how many, and it is pulled with this drone recorded. What the session did
// stays in a list until Done.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePullsApi, type BoxItem, type ResolvedBox } from '../../Parts/Pulls/pullsApi';
import { itemLabel } from '../../components/ItemName';
import { useApi } from '../../apiHelpers';
import { workOrderErrorMessage } from '../../WorkOrders/workOrdersApi';
import { LOOKUP_MISS_TEXT, useStickerLookup } from '../../utils/useStickerLookup';
import { KEY_PRIMARY, KEY_QUIET } from '../Assembly/WorkOrderStation';
import {
  DISPLACED_CHOICES,
  conflictSentence,
  conflictsOf,
  postAssemblyCommit,
  type Conflict,
  type DisplacedTo,
} from '../Assembly/commitFit';
import BoxItemList from '../Box/BoxItemList';
import { pulledForRepair } from '../Box/sentences';
import useScannerInput from '../hooks/useScannerInput';
import { buzz } from '../shell';
import { focusForScan, KeyStrip, Notice, QuantityField, ScanField, ScreenBar, SECTION } from '../ui';
import type { DroneSummary } from './types';

// Module scope so the hook's listeners keep a stable identity.
const isStickerCode = (val: string) => /^\S{4,}$/.test(val);

const tail = (uid: string) => uid.replace(/:/g, '').slice(-6).toUpperCase();

interface Fit {
  value: string;
  /** Only a kind the server reported: anything guessed here could be wrong. */
  slot?: string;
  label: string;
}

type Pending =
  | null
  | { kind: 'box'; box: ResolvedBox }
  | { kind: 'conflict'; fit: Fit; conflicts: Conflict[] };

interface Props {
  summary: DroneSummary;
  operatorId?: string;
  onDone: () => void;
}

const BoxPull: React.FC<{
  box: ResolvedBox;
  serial: string;
  onPulled: (sentence: string) => void;
  onCancel: () => void;
}> = ({ box, serial, onPulled, onCancel }) => {
  const api = usePullsApi();
  const usable = (i: BoxItem) => i.pullable;
  const [itemId, setItemId] = useState<string | null>(
    box.items.length === 1 && usable(box.items[0]) ? box.items[0].receipt_item_id : null,
  );
  const item = box.items.find((i) => i.receipt_item_id === itemId) || null;
  const [qty, setQty] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (message: string) => { buzz('reject'); setError(message); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || busy) return;
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) return fail('Enter how many.');
    if (n > item.quantity_remaining) return fail(`Only ${item.quantity_remaining} left.`);
    setBusy(true);
    setError(null);
    try {
      const r = await api.pull({ receipt_item_id: item.receipt_item_id, quantity: n, reason: 'repair', serialnumber: serial });
      onPulled(pulledForRepair(r, serial));
    } catch (err: any) {
      setBusy(false);
      fail(err?.message || 'The pull did not go through. Try again.');
    }
  };

  return (
    <section aria-label={`Box ${box.barcode}`} className="flex flex-col gap-3">
      <p className="px-1 text-sm text-on-surface-variant">
        Box <span className="font-mono">{box.barcode}</span>
      </p>
      <BoxItemList items={box.items} selectedId={itemId} usable={usable} onSelect={setItemId} />
      {item && (
        <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
          <QuantityField label="Quantity" value={qty} max={item.quantity_remaining} onChange={setQty} autoFocus />
          {error && <div role="alert"><Notice tone="miss">{error}</Notice></div>}
          <div className="flex items-center gap-2">
            <button type="submit" disabled={busy} className={`${KEY_PRIMARY} flex-1`}>
              {busy ? 'Pulling…' : `Pull ${Number(qty) > 0 ? qty : ''} for this drone`}
            </button>
            <button type="button" onClick={onCancel} disabled={busy} className={KEY_QUIET}>Cancel</button>
          </div>
        </form>
      )}
      {!item && (
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 px-1 text-base text-on-surface-variant">
            {box.items.some(usable) ? 'Pick the item to pull.' : 'Nothing in this box can be pulled from where it is.'}
          </p>
          <button type="button" onClick={onCancel} className={KEY_QUIET}>Cancel</button>
        </div>
      )}
    </section>
  );
};

const ConflictPrompt: React.FC<{
  conflicts: Conflict[];
  busy: boolean;
  onConfirm: (displacedTo: DisplacedTo) => void;
  onCancel: () => void;
}> = ({ conflicts, busy, onConfirm, onCancel }) => {
  const [displacedTo, setDisplacedTo] = useState<DisplacedTo>('scrap');
  const replacing = conflicts.some((c) => c.type === 'slot_occupied');
  return (
    <section aria-label="Confirm the fit" className={`${SECTION} flex flex-col gap-3`}>
      <ul className="flex flex-col gap-1 text-base text-on-surface">
        {conflicts.map((c, i) => <li key={i}>{conflictSentence(c, c.slot)}</li>)}
      </ul>
      {replacing && (
        <div>
          <p className="mb-1 text-sm text-on-surface-variant">Where does the old part go?</p>
          <KeyStrip
            label="Where the old part goes"
            options={DISPLACED_CHOICES.map((d) => ({ key: d.value, label: d.label }))}
            value={displacedTo}
            onChange={(k) => setDisplacedTo(k as DisplacedTo)}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy} onClick={() => onConfirm(displacedTo)} className={`${KEY_PRIMARY} flex-1`}>
          {busy ? 'Fitting…' : replacing ? 'Replace it' : 'Move it here'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={KEY_QUIET}>Cancel</button>
      </div>
    </section>
  );
};

const RepairSession: React.FC<Props> = ({ summary, operatorId, onDone }) => {
  const api = useApi();
  const { lookup } = useStickerLookup();
  const serial = summary.drone.serialnumber;
  const scanRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(0);

  const [pending, setPending] = useState<Pending>(null);
  const [working, setWorking] = useState(false);
  const [entries, setEntries] = useState<{ id: number; text: string }[]>([]);
  const [message, setMessage] = useState<{ tone: 'ok' | 'miss'; text: string; n: number } | null>(null);

  const say = useCallback((tone: 'ok' | 'miss', text: string) => {
    if (tone === 'miss') buzz('reject');
    setMessage((prev) => ({ tone, text, n: (prev?.n ?? 0) + 1 }));
  }, []);

  const record = useCallback((text: string) => {
    nextId.current += 1;
    setEntries((prev) => [{ id: nextId.current, text }, ...prev]);
    setMessage(null);
  }, []);

  // The scan field takes the next scan after every outcome.
  useEffect(() => {
    if (!pending) focusForScan(scanRef);
  }, [pending, entries.length, message]);

  const resolveModelCode = useCallback((): string | null => {
    if (summary.drone.model_code) return summary.drone.model_code;
    say('miss', 'This drone has no model on record. Fix it on the drone page first.');
    return null;
  }, [summary, say]);

  const fit = useCallback(async (f: Fit, force?: DisplacedTo | 'move') => {
    const model = resolveModelCode();
    if (!model) return;
    setWorking(true);
    try {
      const body: Record<string, unknown> = {
        components: [{ value: f.value, ...(f.slot ? { slot: f.slot } : {}) }],
        drone_serial: serial,
        model_code: model,
      };
      if (operatorId) body.operator_id = operatorId;
      if (force) {
        body.force = true;
        if (force !== 'move') body.displaced_to = force;
      }
      await postAssemblyCommit(api, body);
      setPending(null);
      record(`${f.label} fitted`);
    } catch (err: any) {
      const conflicts = conflictsOf(err);
      if (conflicts.length > 0) {
        buzz('warn');
        setPending({ kind: 'conflict', fit: f, conflicts });
      } else {
        setPending(null);
        say('miss', workOrderErrorMessage(err, 'The part was not fitted. Try again.'));
      }
    } finally {
      setWorking(false);
    }
  }, [api, serial, operatorId, resolveModelCode, record, say]);

  const handleScan = useCallback(async (raw: string) => {
    if (working) {
      say('miss', `Still working on the last scan. Scan ${raw} again.`);
      return;
    }
    setWorking(true);
    const outcome = await lookup(raw);
    setWorking(false);
    if (outcome.status === 'ignored') return;
    if (outcome.status === 'miss') {
      say('miss', LOOKUP_MISS_TEXT[outcome.miss]);
      return;
    }
    const { kind, payload } = outcome.result as { kind: string; payload: any };
    if (kind === 'box' && payload?.items) {
      setMessage(null);
      setPending({ kind: 'box', box: payload as ResolvedBox });
      return;
    }
    if (kind === 'board' && payload?.primary_uid) {
      await fit({
        value: payload.primary_uid,
        slot: payload.kind_key || undefined,
        label: `${payload.board_type ? `${payload.board_type} board` : 'Board'} ${tail(payload.primary_uid)}`,
      });
      return;
    }
    if (kind === 'part_instance' && payload?.id) {
      await fit({ value: payload.id, slot: payload.kind_key || undefined, label: itemLabel(payload) });
      return;
    }
    say('miss', 'Scan a part or a box. That sticker is neither.');
  }, [working, lookup, fit, say]);

  useScannerInput({
    onScan: handleScan,
    surface: 'lookup',
    minLength: 4,
    validator: isStickerCode,
    disabled: !!pending,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScreenBar
        title={<>Repair <span className="font-mono tabular-nums">{serial}</span></>}
        backLabel="Done"
        onBack={onDone}
      />
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6">
        {!pending && <ScanField inputRef={scanRef} label="Scan a part or a box" busy={working} onSubmit={handleScan} />}

        <div aria-live="polite" className="flex flex-col gap-3">
          {message && (
            <div role={message.tone === 'miss' ? 'alert' : undefined}>
              <Notice key={message.n} tone={message.tone}>{message.text}</Notice>
            </div>
          )}
        </div>

        {pending?.kind === 'box' && (
          <BoxPull
            box={pending.box}
            serial={serial}
            onCancel={() => setPending(null)}
            onPulled={(sentence) => { setPending(null); record(sentence); }}
          />
        )}

        {pending?.kind === 'conflict' && (
          <ConflictPrompt
            conflicts={pending.conflicts}
            busy={working}
            onCancel={() => setPending(null)}
            onConfirm={(displacedTo) =>
              fit(pending.fit, pending.conflicts.some((c) => c.type === 'slot_occupied') ? displacedTo : 'move')}
          />
        )}

        {entries.length > 0 ? (
          <section aria-label="Done this session" className="px-1">
            <h2 className="mb-1 text-sm font-semibold text-on-surface-variant">Done on this drone</h2>
            <ul className="flex flex-col gap-1.5">
              {entries.map((e, i) => (
                <li
                  key={e.id}
                  className={`rounded-lg bg-surface-container-low px-3 py-2 text-base text-on-surface break-words ${i === 0 ? 'motion-safe:animate-rowLand' : ''}`}
                >
                  {e.text}
                </li>
              ))}
            </ul>
          </section>
        ) : (
          !pending && (
            <p className="px-2 text-center text-sm text-on-surface-variant">
              A board or a QR part is fitted to this drone. A box is pulled for it. Each one lands here.
            </p>
          )
        )}
      </div>
    </div>
  );
};

export default RepairSession;
